import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import QRCode from 'react-qr-code';
import StripePaymentForm from '../components/StripePaymentForm';
import PaymentBreakdown from '../components/PaymentBreakdown';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(Number(amount || 0));
};

const PublicCheckout = () => {
  const [invoice, setInvoice] = useState(null);
  const [paymentOptions, setPaymentOptions] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [stripeConfig, setStripeConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFree, setIsFree] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paramsState, setParamsState] = useState({ invoiceId: null, token: null });
  const [venmoPaymentInfo, setVenmoPaymentInfo] = useState(null);
  const [venmoConfirmed, setVenmoConfirmed] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const invoiceId = params.get('invoiceId');
    const token = params.get('token');
    setParamsState({ invoiceId, token });

    if (!invoiceId || !token) {
      setError('Missing checkout parameters');
      setLoading(false);
      return;
    }

    (async () => {
      try {
        const res = await axios.get(`/api/public/invoices/${invoiceId}/checkout/${encodeURIComponent(token)}`);
        setInvoice(res.data.invoice);
        setPaymentOptions(res.data.paymentOptions || {});
        setIsFree(res.data.isFree || false);

        if (res.data.isFree) {
          setTimeout(() => {
            window.location.href = '/public/thanks?registration=success';
          }, 2000);
        }
      } catch (err) {
        console.error(err);
        setError(err.response?.data?.message || 'Failed to load checkout');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const availableMethods = useMemo(() => {
    const methods = [];
    if (paymentOptions?.stripe?.available) methods.push('stripe');
    if (paymentOptions?.venmo?.available) methods.push('venmo');
    return methods;
  }, [paymentOptions]);

  useEffect(() => {
    if (availableMethods.length && !selectedMethod) {
      setSelectedMethod(availableMethods[0]);
    }
  }, [availableMethods, selectedMethod]);

  useEffect(() => {
    if (selectedMethod !== 'stripe') {
      setStripeConfig(null);
    }
    if (selectedMethod !== 'venmo') {
      setVenmoPaymentInfo(null);
    }
  }, [selectedMethod]);

  const initializeStripePayment = async () => {
    if (!paramsState.invoiceId || !paramsState.token || !invoice) return;
    setIsProcessing(true);
    try {
      const res = await axios.post(`/api/public/invoices/${encodeURIComponent(paramsState.invoiceId)}/stripe-payment-intent`, {
        token: paramsState.token
      });

      setStripeConfig({
        clientSecret: res.data.clientSecret,
        publishableKey: res.data.publishableKey,
        amount: res.data.amount,
        currency: res.data.currency || 'usd',
        breakdown: res.data.breakdown || null
      });
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Unable to start card payment. Please try another method.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleStripeComplete = async (result) => {
    if (!result?.success || !result.paymentIntent) {
      if (result?.error) {
        toast.error(result.error || 'Card payment was not completed.');
      }
      return;
    }

    try {
      await axios.post(`/api/public/invoices/${encodeURIComponent(paramsState.invoiceId)}/stripe-confirm`, {
        token: paramsState.token,
        paymentIntentId: result.paymentIntent.id
      });

      toast.success('Payment complete! Thank you.');
      setStripeConfig(null);
      setInvoice((prev) => prev ? { ...prev, status: 'paid', paid_date: new Date().toISOString() } : prev);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Failed to confirm payment. Please contact the organizer.');
    }
  };


  const initializeVenmoPayment = async () => {
    if (!paramsState.invoiceId || !paramsState.token || !invoice) return;
    setIsProcessing(true);
    try {
      const res = await axios.post(
        `/api/public/invoices/${encodeURIComponent(paramsState.invoiceId)}/venmo/${encodeURIComponent(paramsState.token)}`
      );
      setVenmoPaymentInfo(res.data);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Unable to start Venmo payment. Please try another method.');
    } finally {
      setIsProcessing(false);
    }
  };

  const confirmVenmoPayment = async () => {
    if (!paramsState.invoiceId || !paramsState.token) return;
    setIsProcessing(true);
    try {
      await axios.post(
        `/api/public/invoices/${encodeURIComponent(paramsState.invoiceId)}/venmo-pending/${encodeURIComponent(paramsState.token)}`
      );
      setVenmoConfirmed(true);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Failed to record payment. Please contact your coach directly.');
    } finally {
      setIsProcessing(false);
    }
  };

  const renderPaymentMethodOptions = () => {
    if (!paymentOptions) {
      return null;
    }

    const options = [];
    if (paymentOptions.stripe?.available) {
      options.push({
        id: 'stripe',
        title: 'Credit or Debit Card',
        description: 'Pay securely online with Stripe.'
      });
    }
    if (paymentOptions.venmo?.available) {
      options.push({
        id: 'venmo',
        title: 'Venmo',
        description: `Send payment directly to @${paymentOptions.venmo.username || 'coach'} via the Venmo app or website.`
      });
    }

    if (options.length === 0) {
      return (
        <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
          Online payment methods are not available for this invoice. Please contact the coach or administrator for assistance.
        </div>
      );
    }

    return (
      <div className="mt-6 space-y-3">
        <div className="text-sm font-semibold text-gray-700">Choose payment method</div>
        <div className="space-y-3">
          {options.map(option => (
            <label
              key={option.id}
              className={`flex items-start gap-3 border rounded-lg px-4 py-3 cursor-pointer transition ${
                selectedMethod === option.id ? 'border-primary-500 bg-primary-50 shadow-sm' : 'border-gray-200 hover:border-primary-300'
              }`}
            >
              <input
                type="radio"
                name="public-invoice-payment-method"
                value={option.id}
                checked={selectedMethod === option.id}
                onChange={() => setSelectedMethod(option.id)}
                className="mt-1"
              />
              <div>
                <div className="text-sm font-semibold text-gray-900">{option.title}</div>
                {option.description && <div className="text-xs text-gray-600 mt-1">{option.description}</div>}
              </div>
            </label>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-gray-600">Loading…</div>;
  }

  if (error) {
    return <div className="min-h-screen flex items-center justify-center text-red-600">{error}</div>;
  }

  if (!invoice) {
    return null;
  }

  const isPaid = invoice.status === 'paid';

  if ((isFree || isPaid) && invoice) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-2xl mx-auto px-4 py-10">
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <div className="mb-4">
              <svg className="mx-auto h-16 w-16 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {isPaid ? 'Payment Received!' : 'Registration Successful!'}
            </h1>
            <p className="text-gray-600 mb-4">
              {isPaid
                ? `Invoice ${invoice.invoice_number || invoice.id} has already been paid.`
                : `Your registration for ${invoice.title} is complete.`}
            </p>
            <p className="text-sm text-gray-500">
              {isPaid ? 'Thank you for your payment.' : 'This invoice does not require a payment.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto px-4 py-10">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Invoice #{invoice.invoice_number || invoice.id}</h1>
              <p className="text-sm text-gray-600 mt-1">{invoice.title}</p>
            </div>
            <div className="text-right">
              <div className="text-xs text-gray-500">Amount Due</div>
              <div className="text-xl font-semibold text-gray-900">{formatCurrency(invoice.total_amount)}</div>
            </div>
          </div>

          <div className="mt-4 border-t border-gray-100 pt-4 text-sm text-gray-600">
            <p><strong>Due Date:</strong> {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : 'N/A'}</p>
            <p className="mt-1">
              <strong>Status:</strong> <span className="capitalize">{invoice.status}</span>
            </p>
          </div>

          {renderPaymentMethodOptions()}

          {selectedMethod === 'stripe' && !stripeConfig && paymentOptions?.stripe?.available && (
            <div className="mt-6">
              <button
                onClick={initializeStripePayment}
                disabled={isProcessing}
                className="w-full sm:w-auto px-6 py-2 rounded-md bg-primary-600 text-white hover:bg-primary-700 transition-colors disabled:opacity-60"
              >
                {isProcessing ? 'Preparing secure form…' : 'Start Secure Card Payment'}
              </button>
            </div>
          )}

          {stripeConfig && (
            <div className="mt-8">
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded">
                <p className="text-sm text-green-800">
                  Complete the secure payment below to finalize your payment. Once the payment succeeds, you will receive confirmation immediately.
                </p>
              </div>
              <PaymentBreakdown breakdown={stripeConfig.breakdown} baseLabel="Invoice amount" />
              <StripePaymentForm
                config={stripeConfig}
                onComplete={handleStripeComplete}
              />
            </div>
          )}

          {selectedMethod === 'venmo' && !venmoPaymentInfo && paymentOptions?.venmo?.available && (
            <div className="mt-6">
              <button
                type="button"
                onClick={initializeVenmoPayment}
                disabled={isProcessing}
                className="w-full sm:w-auto px-6 py-2 rounded-md bg-[#3D95CE] text-white hover:bg-[#357ab8] transition-colors disabled:opacity-60"
              >
                {isProcessing ? 'Setting up Venmo…' : 'Pay with Venmo'}
              </button>
            </div>
          )}

          {venmoPaymentInfo && !venmoConfirmed && (
            <div className="mt-8 p-5 bg-blue-50 border border-blue-200 rounded-lg space-y-5">
              <div>
                <div className="text-base font-semibold text-gray-900 mb-1">Pay via Venmo</div>
                <div className="text-sm text-gray-700">Scan the QR code with your Venmo app to send payment to <span className="font-semibold">@{venmoPaymentInfo.username}</span>.</div>
              </div>
              <div className="flex justify-center bg-white p-4 rounded-lg border border-blue-100">
                <QRCode
                  value={venmoPaymentInfo.webUrl}
                  size={180}
                  bgColor="#ffffff"
                  fgColor="#000000"
                />
              </div>
              <div className="text-sm text-gray-700 space-y-1">
                <div>Amount: <span className="font-semibold">{formatCurrency(venmoPaymentInfo.amount)}</span></div>
                {venmoPaymentInfo.note && <div>Note: <span className="italic">{venmoPaymentInfo.note}</span></div>}
              </div>
              <div className="border-t border-blue-200 pt-4">
                <p className="text-xs text-gray-500 mb-3">Once you've completed the payment in Venmo, tap the button below. Your coach will be notified and will mark the invoice as paid after verifying.</p>
                <button
                  type="button"
                  onClick={confirmVenmoPayment}
                  disabled={isProcessing}
                  className="w-full px-4 py-3 rounded-md bg-green-600 text-white font-semibold hover:bg-green-700 transition-colors disabled:opacity-60"
                >
                  {isProcessing ? 'Recording…' : "I've Completed My Venmo Payment"}
                </button>
              </div>
            </div>
          )}

          {venmoConfirmed && (
            <div className="mt-8 p-6 bg-green-50 border border-green-200 rounded-lg text-center space-y-2">
              <svg className="mx-auto h-12 w-12 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-base font-semibold text-gray-900">Payment Submitted!</div>
              <p className="text-sm text-gray-600">Your coach has been notified and will verify your Venmo payment. You'll receive confirmation once it's marked as paid.</p>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default PublicCheckout;


